/**
 * Timing utilities for streaming text animation.
 *
 * ## How Timing Works
 *
 * The animation calculates delay for each token based on multiple factors:
 *
 * 1. **Base Delay**: Starting point for all tokens (default: 70ms)
 * 2. **Variant Multiplier**: Larger text (h1-h4) animates slower, smaller text faster
 * 3. **Token Length**: Longer words get slightly more time (+4ms per char over 6, max +30ms)
 * 4. **Punctuation Pause**: Extra delay after sentence-ending punctuation
 * 5. **Newline Pause**: Extra delay after line breaks
 * 6. **Random Jitter**: Optional variance for natural feel
 * 7. **Thinking Pauses**: Optional random "hesitation" like ChatGPT
 *
 * A `maxTypingDurationMs` cap can short-circuit all of the above: once it
 * elapses the remaining tokens are revealed together instead of one-by-one.
 *
 * ## Variant Speed Multipliers
 *
 * The idea is that larger text has fewer words visible at once, so it should
 * animate slower to give the reader time to track. Smaller body text can
 * animate faster since the eye can scan more quickly.
 *
 * | Variant   | Multiplier | Effect                    |
 * |-----------|------------|---------------------------|
 * | h1        | 1.4x       | 40% slower than baseline  |
 * | h2        | 1.3x       | 30% slower                |
 * | h3        | 1.2x       | 20% slower                |
 * | h4        | 1.1x       | 10% slower                |
 * | h5        | 1.0x       | Baseline speed            |
 * | h6        | 0.95x      | 5% faster                 |
 * | subtitle  | 0.85-0.9x  | 10-15% faster             |
 * | body      | 0.8-0.85x  | 15-20% faster             |
 */
import { TypographyProps } from '@rhg/libs/material';
import { StreamingTimingConfig, StreamingToken } from './types';

/** Matches sentence-ending and clause punctuation at end of token */
const PUNCTUATION_REGEX = /[.!?,;:…—]+$/;

/**
 * Default timing configuration. These values are multiplied by the variant
 * speed multiplier, except for probability which stays as-is.
 */
export const DEFAULT_TIMING_CONFIG: Required<StreamingTimingConfig> = {
    baseDelayMs: 70,
    jitterMs: 0,
    punctuationDelayMs: 100,
    newlineDelayMs: 180,
    thinkingPauseProbability: 0,
    thinkingPauseRange: [100, 200],
    tokenAnimationDurationMs: 400,
    maxTypingDurationMs: 0,
    bulkRevealDurationMs: 500,
};

/**
 * Speed multipliers based on typography variant.
 * Larger text = slower (higher multiplier), smaller text = faster (lower multiplier).
 */
const VARIANT_SPEED_MULTIPLIERS: Partial<
    Record<NonNullable<TypographyProps['variant']>, number>
> = {
    h1: 1.4,
    h2: 1.3,
    h3: 1.2,
    h4: 1.1,
    h5: 1.0,
    h6: 0.95,
    subtitle1: 0.9,
    subtitle2: 0.85,
    body1: 0.85,
    body2: 0.8,
};

/**
 * Gets speed multiplier for a typography variant.
 */
export function getVariantSpeedMultiplier(
    variant?: TypographyProps['variant']
): number {
    if (!variant) {
        return 1.0;
    }
    return VARIANT_SPEED_MULTIPLIERS[variant] ?? 1.0;
}

/**
 * Merges user timing config with defaults, adjusting for variant.
 * Larger variants (h1-h4) get slower timing, smaller variants (h5+) get faster timing.
 */
export function mergeTimingConfig(
    config?: StreamingTimingConfig,
    variant?: TypographyProps['variant']
): Required<StreamingTimingConfig> {
    const multiplier = getVariantSpeedMultiplier(variant);

    return {
        baseDelayMs: Math.round(
            (config?.baseDelayMs ?? DEFAULT_TIMING_CONFIG.baseDelayMs) *
                multiplier
        ),
        jitterMs: Math.round(
            (config?.jitterMs ?? DEFAULT_TIMING_CONFIG.jitterMs) * multiplier
        ),
        punctuationDelayMs: Math.round(
            (config?.punctuationDelayMs ??
                DEFAULT_TIMING_CONFIG.punctuationDelayMs) * multiplier
        ),
        newlineDelayMs: Math.round(
            (config?.newlineDelayMs ?? DEFAULT_TIMING_CONFIG.newlineDelayMs) *
                multiplier
        ),
        thinkingPauseProbability:
            config?.thinkingPauseProbability ??
            DEFAULT_TIMING_CONFIG.thinkingPauseProbability,
        thinkingPauseRange:
            config?.thinkingPauseRange ??
            DEFAULT_TIMING_CONFIG.thinkingPauseRange,
        tokenAnimationDurationMs: Math.round(
            (config?.tokenAnimationDurationMs ??
                DEFAULT_TIMING_CONFIG.tokenAnimationDurationMs) * multiplier
        ),
        // Deliberately not variant-adjusted: these are absolute caps/durations
        // from the design spec, so they must stay the same wall-clock time
        // regardless of the typography variant.
        maxTypingDurationMs:
            config?.maxTypingDurationMs ??
            DEFAULT_TIMING_CONFIG.maxTypingDurationMs,
        bulkRevealDurationMs:
            config?.bulkRevealDurationMs ??
            DEFAULT_TIMING_CONFIG.bulkRevealDurationMs,
    };
}

/**
 * Calculates the delay before revealing a token.
 *
 * The delay is built up from multiple factors to create natural-feeling animation:
 * - Base delay provides the rhythm
 * - Jitter adds human-like variance
 * - Long words get extra time (readers need more time to parse them)
 * - Punctuation creates natural sentence breaks
 * - Newlines create paragraph pauses
 * - Random thinking pauses mimic AI "consideration"
 *
 * @param token - The token about to be revealed
 * @param prevToken - The previously revealed token (for punctuation/newline detection)
 * @param config - Timing configuration (already adjusted for variant)
 * @returns Delay in milliseconds (minimum 8ms to prevent overwhelming the browser)
 */
export function calculateTokenDelay(
    token: StreamingToken,
    prevToken: StreamingToken | null,
    config: Required<StreamingTimingConfig>
): number {
    let delay = config.baseDelayMs;

    // Jitter: random variance centered on 0 (range: -jitterMs to +jitterMs)
    delay += Math.random() * config.jitterMs * 2 - config.jitterMs;

    // Token length factor: +4ms per character over 6, capped at +30ms
    // e.g., "hello" (5 chars) = +0ms, "extraordinary" (13 chars) = +28ms
    delay += Math.min(30, Math.max(0, token.content.length - 6) * 4);

    // Extra pause after punctuation in previous token
    if (prevToken && PUNCTUATION_REGEX.test(prevToken.content)) {
        delay += config.punctuationDelayMs;
    }

    // Extra pause after newlines
    if (prevToken?.isNewline) {
        delay += config.newlineDelayMs;
    }

    // Random "thinking" pause (like ChatGPT's occasional hesitation)
    if (Math.random() < config.thinkingPauseProbability) {
        const [min, max] = config.thinkingPauseRange;
        delay += min + Math.random() * (max - min);
    }

    return Math.max(8, Math.round(delay));
}
